import React, { useState } from "react";
import {
  ActivityIndicator,
  Image,
  ImageBackground,
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
} from "react-native";
import {
  AvatarModalPng,
  NamePng,
  ProfileScore,
  RedChipsPng,
} from "../../../../assets";
import { useDevice } from "../../../../utils/hooks/useDevice";

type Props = {
  name: string;
  score: number;
  chips: string;
  avatarImage: any;
  backgroundImage: any;
  inviteSent: boolean;
  onInvite: () => void;
};

const InvitedPlayerForTableCard = ({
  name,
  score,
  chips,
  avatarImage,
  backgroundImage,
  onInvite,
}: Props) => {
  const { isMobile, isDesktop } = useDevice();
  const [isInviting, setIsInviting] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [nameTextWidth, setNameTextWidth] = useState(0);

  const handleInviteClick = () => {
    if (isInviting || inviteSent) return;

    setIsInviting(true);
    onInvite();

    setTimeout(() => {
      setIsInviting(false);
      setInviteSent(true);
    }, 500);
  };

  const isSmall = isMobile;
  const marginLeft = isSmall ? 6 : isDesktop ? 12 : 8;

  // Calculate padding for background edges
  const namePadding = isSmall ? 16 : isDesktop ? 40 : 30;
  const nameBackgroundWidth =
    nameTextWidth > 0 ? nameTextWidth + namePadding : undefined;

  return (
    <View style={[styles.cardBackground, { width: "100%" }]}>
      <ImageBackground
        source={backgroundImage}
        resizeMode="stretch"
        style={[styles.cardBackground, { flex: 1 }]}
      >
        <View style={[styles.container, { flex: 1 }]}>
          {/* Avatar */}
          <View
            style={[
              styles.avatarWrapper,
              {
                width: isSmall ? 70 : isDesktop ? 100 : 95,
                marginLeft: isSmall ? -12 : isDesktop ? -2 : -2,
              },
            ]}
          >
            <ImageBackground
              source={AvatarModalPng}
              style={[
                styles.avatarBg,
                {
                  width: isSmall ? 60 : isDesktop ? 100 : 80,
                  height: isSmall ? 45 : isDesktop ? 75 : 60,
                },
              ]}
              resizeMode="contain"
            />
            <Image
              source={avatarImage}
              style={[
                styles.avatarImage,
                {
                  width: isSmall ? 56 : isDesktop ? 96 : 76,
                  height: isSmall ? 35 : isDesktop ? 58 : 46,
                  top: isSmall ? 5 : isDesktop ? 10 : 7,
                  left: isSmall ? 8 : isDesktop ? 0 : 5,
                },
              ]}
              resizeMode="contain"
            />
            <Image
              source={ProfileScore}
              style={[
                styles.scoreBadge,
                {
                  width: isSmall ? 24 : isDesktop ? 28 : 30,
                  height: isSmall ? 24 : isDesktop ? 28 : 30,
                  top: isSmall ? 12 : isDesktop ? 25 : 18,
                  left: isSmall ? 50 : isDesktop ? 70 : 65,
                },
              ]}
              resizeMode="contain"
            />
            <Text
              style={[
                styles.scoreText,
                {
                  fontSize: isSmall ? 10 : isDesktop ? 12 : 12,
                  top: isSmall ? 16 : isDesktop ? 31 : 23,
                  left: isSmall ? 58 : isDesktop ? 80 : 73,
                },
              ]}
            >
              {score}
            </Text>
          </View>

          {/* Name */}
          <View style={[styles.nameWrapper, { marginLeft }]}>
            <ImageBackground
              source={NamePng}
              style={[
                styles.nameBg,
                {
                  width: nameBackgroundWidth,
                  height: isSmall ? 24 : isDesktop ? 32 : 28,
                  minWidth: isSmall ? 60 : isDesktop ? 100 : 80,
                },
              ]}
              resizeMode="stretch"
            >
              <Text
                style={[
                  styles.nameText,
                  {
                    fontSize: isSmall ? 11 : isDesktop ? 16 : 13,
                    paddingHorizontal: isSmall ? 8 : isDesktop ? 6 : 15,
                    paddingTop: isSmall ? 2 : isDesktop ? 1 : 3,
                  },
                ]}
                numberOfLines={1}
                ellipsizeMode="tail"
                onTextLayout={(e) => {
                  const { width } = e.nativeEvent.lines[0] || { width: 0 };
                  setNameTextWidth(width);
                }}
              >
                {name}
              </Text>
            </ImageBackground>
          </View>

          {/* Chips */}
          <View style={[styles.statsItem, { marginLeft }]}>
            <Image
              source={RedChipsPng}
              resizeMode="contain"
              style={[
                styles.statsIcon,
                {
                  width: isSmall ? 18 : isDesktop ? 28 : 22,
                  height: isSmall ? 18 : isDesktop ? 28 : 22,
                },
              ]}
            />
            <Text
              style={[
                styles.statsText,
                {
                  fontSize: isSmall ? 11 : isDesktop ? 14 : 12,
                },
              ]}
            >
              {chips}
            </Text>
          </View>
        </View>
      </ImageBackground>

      {/* Invite Button */}
      <TouchableOpacity
        style={[
          styles.inviteButton,
          {
            paddingHorizontal: isSmall ? 10 : isDesktop ? 16 : 12,
            paddingVertical: isSmall ? 5 : isDesktop ? 8 : 6,
            marginLeft: isSmall ? 6 : 10,
            marginRight: isSmall ? 6 : 10,
            minWidth: isSmall ? 50 : isDesktop ? 80 : 65,
          },
          (isInviting || inviteSent) && styles.inviteButtonDisabled,
        ]}
        onPress={handleInviteClick}
        disabled={isInviting || inviteSent}
        activeOpacity={0.7}
      >
        {isInviting ? (
          <ActivityIndicator color="#FFF" size="small" />
        ) : (
          <Text
            style={[
              styles.inviteButtonText,
              {
                fontSize: isSmall ? 10 : isDesktop ? 14 : 12,
              },
            ]}
          >
            {inviteSent ? "Sent" : "Invite"}
          </Text>
        )}
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 2,
    justifyContent: "space-between",
  },
  cardBackground: {
    borderRadius: 12,
    alignItems: "center",
    flexDirection: "row",
    overflow: "hidden",
    minHeight: 50,
  },
  container: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  avatarWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  avatarBg: {
    position: "relative",
  },
  avatarImage: {
    position: "absolute",
  },
  scoreBadge: {
    position: "absolute",
  },
  scoreText: {
    position: "absolute",
    color: "#fff",
    fontWeight: "600",
    textAlign: "center",
  },
  nameWrapper: {
    position: "relative",
    flexShrink: 1,
  },
  nameBg: {
    justifyContent: "center",
    alignItems: "flex-start",
  },
  nameText: {
    color: "white",
    fontWeight: "500",
  },
  statsItem: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 1,
  },
  statsIcon: {
    // Dynamic sizing in component
  },
  statsText: {
    color: "white",
    marginLeft: 2,
    fontWeight: "bold",
  },
  inviteButton: {
    backgroundColor: "#28A745",
    borderColor: "#a72828ff",
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 3,
    elevation: 5,
  },
  inviteButtonDisabled: {
    backgroundColor: "#6C757D",
    opacity: 0.7,
  },
  inviteButtonText: {
    color: "#FFF",
    fontWeight: "bold",
  },
});

export default InvitedPlayerForTableCard;
